import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus,
  Search,
  Filter,
  Calendar,
  MapPin,
  Clock,
  Edit,
  Trash2,
  Download,
  Building,
  Copy
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Flights = () => {
  const { user } = useAuth();

  const initialOrgId = user?.role === 'superadmin' ? '' : user?.org_id;

  const [filters, setFilters] = useState({
    search: '',
    operation_type: '',
    org_id: initialOrgId,
    start_date: '',
    end_date: '',
    page: 1,
    limit: 10
  });

  const [showFilters, setShowFilters] = useState(false);
  const [deleteFlightId, setDeleteFlightId] = useState(null);

  const { data: orgsData } = useQuery(
    'organizations',
    () => api.get('/admin/orgs').then(res => res.data),
    {
      enabled: user?.role === 'superadmin'
    }
  );

  const effectiveFilters = useMemo(() => {
    const result = { ...filters };
    if (user?.role !== 'superadmin') {
      result.org_id = user?.org_id;
    }
    return result;
  }, [filters, user]);

  const { data, isLoading, refetch } = useQuery(
    ['flights', effectiveFilters],
    () => {
      const params = new URLSearchParams();
      Object.entries(effectiveFilters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      return api.get(`/flights?${params.toString()}`).then(res => res.data);
    },
    { keepPreviousData: true }
  );

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset page when filtering
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const formatTime = (minutes) => {
    if (!minutes) return '0h 0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const downloadPdf = async (flightId) => {
    try {
      const fileName = `voo_${flightId}.pdf`;
      const res = await api.get(`/flights/${flightId}/pdf`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
    }
  };

  const queryClient = useQueryClient();

  const deleteMutation = useMutation(
    async (flightId) => {
      return await api.delete(`/flights/${flightId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('flights');
        toast.success('Voo excluído com sucesso!');
      },
      onError: (error) => {
        const serverError = error?.response?.data?.error || error?.response?.data?.message;
        toast.error(serverError || 'Erro ao excluir voo');
      }
    }
  );

  const handleDeleteFlight = (e, flightId) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteFlightId(flightId);
  };

  const confirmDeleteFlight = () => {
    if (!deleteFlightId) return;
    deleteMutation.mutate(deleteFlightId, {
      onSettled: () => setDeleteFlightId(null)
    });
  };

  const handleCloneFlight = async (e, flightId) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const response = await api.post(`/flights/${flightId}/clone`);
      const clonedData = response.data.data;

      // Salvar dados no sessionStorage para uso no formulário
      sessionStorage.setItem('clonedFlightData', JSON.stringify(clonedData));
      sessionStorage.setItem('clonedFlightId', flightId);

      // Redirecionar para formulário de criação
      window.location.href = '/flights/new?clone=true';
    } catch (error) {
      const errorMessage = error?.response?.data?.error || 'Erro ao clonar voo';
      toast.error(errorMessage);
    }
  };

  const flights = data?.flights || [];
  const pagination = data?.pagination || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Voos</h1>
          <p className="text-gray-600">Gerencie todos os registros de voo</p>
        </div>
        <div className="flex space-x-3">
          <Link to="/flights/new" className="btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Novo Voo
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Buscar por código, local ou observações..."
                  className="input pl-10"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtros
            </button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {user?.role === 'superadmin' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Empresa
                    </label>
                    <select
                      className="input"
                      value={filters.org_id}
                      onChange={(e) => handleFilterChange('org_id', e.target.value)}
                    >
                      <option value="">Todas</option>
                      {orgsData?.map(org => (
                        <option key={org.id} value={org.id}>{org.name || org.company_name || org.id}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Operação
                  </label>
                  <select
                    className="input"
                    value={filters.operation_type}
                    onChange={(e) => handleFilterChange('operation_type', e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="Pulverização">Pulverização</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Inicial
                  </label>
                  <input
                    type="date"
                    className="input"
                    value={filters.start_date}
                    onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Final
                  </label>
                  <input
                    type="date"
                    className="input"
                    value={filters.end_date}
                    onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Flights Table */}
      <div className="card">
        <div className="card-body p-0">
          {isLoading ? (
            <div className="p-6">
              <LoadingSpinner />
            </div>
          ) : flights.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Data</th>
                      <th className="table-header-cell">Local</th>
                      <th className="table-header-cell">Tipo de Serviço realizado</th>
                      <th className="table-header-cell">Duração</th>
                      <th className="table-header-cell">Área</th>
                      {user?.role === 'superadmin' && (
                        <th className="table-header-cell">Empresa</th>
                      )}
                      <th className="table-header-cell">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {flights.map((flight) => (
                      <tr key={flight.id}>
                        <td className="table-cell">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                            {formatDate(flight.flight_date)}
                          </div>
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center">
                            <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                            <div>
                              <div className="font-medium">{flight.location_name || '-'}</div>
                              {flight.coordinates && (
                                <div className="text-sm text-gray-500">
                                  {flight.coordinates}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="table-cell">
                          <span className="badge-primary">{flight.service_type}</span>
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 text-gray-400 mr-2" />
                            {formatTime(flight.duration_minutes)}
                          </div>
                        </td>
                        <td className="table-cell">
                          {flight.area_hectares ? `${flight.area_hectares} ha` : '-'}
                        </td>
                        {user?.role === 'superadmin' && (
                          <td className="table-cell">
                            <div className="flex items-center">
                              <Building className="h-4 w-4 text-gray-400 mr-2" />
                              {flight.organization_name || '-'}
                            </div>
                          </td>
                        )}
                        <td className="table-cell">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => downloadPdf(flight.id)}
                              className="text-blue-600 hover:text-blue-500"
                              title="Baixar PDF"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                            <button
                              data-testid="clone-button"
                              onClick={(e) => handleCloneFlight(e, flight.id)}
                              className="text-green-600 hover:text-green-500"
                              title="Clonar voo"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                            <Link
                              to={`/flights/${flight.id}/edit`}
                              className="text-primary-600 hover:text-primary-500"
                            >
                              <Edit className="h-4 w-4" />
                            </Link>
                            <button
                              onClick={(e) => handleDeleteFlight(e, flight.id)}
                              className="text-danger-600 hover:text-danger-500"
                              disabled={deleteMutation.isLoading}
                              title="Excluir voo"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Mostrando {((pagination.current_page - 1) * pagination.per_page) + 1} a{' '}
                      {Math.min(pagination.current_page * pagination.per_page, pagination.total)} de{' '}
                      {pagination.total} resultados
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                        disabled={pagination.current_page === 1}
                        className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                        disabled={pagination.current_page === pagination.total_pages}
                        className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Próximo
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p-6 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-sm font-medium text-gray-900 mb-2">Nenhum voo encontrado</h3>
              <p className="text-sm text-gray-500 mb-4">
                {filters.search || filters.operation_type || filters.start_date || filters.end_date
                  ? 'Tente ajustar os filtros de busca'
                  : 'Comece registrando seu primeiro voo'
                }
              </p>
              <Link to="/flights/new" className="btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                Registrar Voo
              </Link>
            </div>
          )}
        </div>
      </div>
      {deleteFlightId && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Excluir voo</h3>
                <p className="text-sm text-gray-600">
                  Tem certeza que deseja excluir este voo? Esta ação não pode ser desfeita.
                </p>
              </div>
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setDeleteFlightId(null)}
                  disabled={deleteMutation.isLoading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={confirmDeleteFlight}
                  disabled={deleteMutation.isLoading}
                >
                  {deleteMutation.isLoading ? 'Excluindo...' : 'Excluir'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Flights;
